import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { OrderApproval } from '../../api/models/order-approval.models';
import { OrderApprovalStatusType, OrderEntryHeader } from '../../api/models/order-entry-header.models';
import { getOrderBasicFromOrderEntryHeader, OrderBasic } from '../../models';
import { RootState } from '../store';

const orderApprovalsAdapter = createEntityAdapter<OrderEntryHeader>({
  selectId: (order: OrderEntryHeader) => order.OrderEntryHeaderId,
});

type OrderApprovalState = {
  hasShownDialogOnHome: boolean;
  orderApprovalInfo?: OrderApproval;
  isLoadingApprovalInfo: boolean;
  orderApprovals: EntityState<OrderEntryHeader>;
  isLoadingApprovals: boolean;
};

const initialState: OrderApprovalState = {
  hasShownDialogOnHome: false,
  orderApprovalInfo: undefined,
  isLoadingApprovalInfo: false,
  orderApprovals: orderApprovalsAdapter.getInitialState(),
  isLoadingApprovals: false,
};

export const orderApprovalSlice = createSlice({
  name: 'orderApproval',
  initialState,
  reducers: {
    resetState: () => initialState,
    setHasShownDialogOnHome: (state: OrderApprovalState, action: PayloadAction<boolean>) => {
      state.hasShownDialogOnHome = action.payload;
    },
    setOrderApprovals: (state: OrderApprovalState, action: PayloadAction<OrderEntryHeader[]>) => {
      orderApprovalsAdapter.setAll(state.orderApprovals, action.payload);
    },
    removeOrderApproval: (state: OrderApprovalState, action: PayloadAction<string>) => {
      orderApprovalsAdapter.removeOne(state.orderApprovals, action.payload);
    },
    setApprovalInfo: (state: OrderApprovalState, action: PayloadAction<OrderApproval>) => {
      state.orderApprovalInfo = action.payload;
    },
    setIsLoadingApprovals: (state: OrderApprovalState, action: PayloadAction<boolean>) => {
      state.isLoadingApprovals = action.payload;
    },
    setIsLoadingApprovalInfo: (state: OrderApprovalState, action: PayloadAction<boolean>) => {
      state.isLoadingApprovalInfo = action.payload;
    },
  },
});

export const { selectAll: selectOrderApprovals, selectById: selectOrderApprovalById } =
  orderApprovalsAdapter.getSelectors<RootState>((state) => state.orderApproval.orderApprovals);

export const selectOrdersByApprovalStatus = createSelector(
  [selectOrderApprovals, (state: RootState, orderApprovalStatus: OrderApprovalStatusType) => orderApprovalStatus],
  (orderApprovals, orderApprovalStatus) => {
    const result: OrderBasic[] = [];

    for (const order of orderApprovals) {
      if (order.OrderApprovalStatusType === orderApprovalStatus) {
        result.push(getOrderBasicFromOrderEntryHeader(order));
      }
    }

    return result;
  }
);
